import {
  LoginOtpVerificationErrorType,
  useLoginOtpVerifification,
  type LoginOtpVerificationInput,
  type LoginOtpVerificationOutput,
} from '@egw/api'
import { useAuth } from '@egw/auth'
import { Button, Input } from '@egw/components'
import { WEALTH_ROUTES } from '@egw/constants'
import { sendEvent } from '@egw/events'
import { useTranslation } from '@egw/i18n'
import { log } from '@egw/logger'
import { stripSpaces } from '@egw/utils'
import { CheckCircleIcon } from '@heroicons/react/20/solid'
import { useEffect, useState, type FC } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router'

interface FormData {
  otpCode: string
}

export const SignInChallenge: FC = () => {
  const { t } = useTranslation('features-auth')
  const [codeResent, setCodeResent] = useState<boolean>(false)
  const navigate = useNavigate()

  const { authenticateWithUserId } = useAuth()

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors: formErrors, isValid },
  } = useForm({
    defaultValues: {
      otpCode: '',
    },
  })

  useEffect(() => {
    sendEvent('user_signInVerificationMethod_click', {
      eventSource: 'egWealth',
      category: 'signIn',
      subCategory: 'signle-factor',
      verificationType: 'phone',
    })
  }, [])

  // SEND USER TO ERROR PAGE
  const onError = (): void => {
    sendEvent('eg_signIn_error', {
      eventSource: 'egWealth',
      category: 'signIn',
      subCategory: 'result',
      verificationType: 'phone',
    })
    void navigate(WEALTH_ROUTES.SIGN_IN_CHALLENGE_FAILURE.path)
  }

  const onSuccess:
    | ((_data: LoginOtpVerificationOutput, _input?: LoginOtpVerificationInput | undefined) => void)
    | undefined = (data) => {
    if (data.__typename === 'LoginOtpVerification') {
      // If user attempted to resend otp setCodeResent to true to see green checkmark
      if (data.isManualReviewApplicant) {
        log.debug('User application under review.')
        void navigate(WEALTH_ROUTES.APPLICATION_UNDER_REVIEW.path)
        return
      }

      if (data.userId) {
        log.debug('user successfully verifyied otp')
        sendEvent('eg_signInVerification_success', {
          eventSource: 'egWealth',
          category: 'signIn',
          subCategory: 'result',
          verificationType: 'phone',
          userId: data.userId,
          authMethod: '1-factor passkey',
        })

        // This method is specifically for Authentication via OTP Challenge
        // This method is used to set the userId in the context of AuthProvider
        authenticateWithUserId(data.userId)

        void navigate(WEALTH_ROUTES.OVERVIEW.path)

        return
      }
    }

    // SET CODE WAS RESENT FOR GREEN VERIFICATION
    if (data.__typename === 'ResendOtp') {
      setCodeResent(true)
      return
    }

    if (data.__typename === 'LoginOtpVerificationError') {
      const errorType = data.errorType

      sendEvent('eg_signIn_fail', {
        eventSource: 'egWealth',
        category: 'signIn',
        subCategory: 'result',
        verificationType: 'phone',
      })

      // INVALID CODE
      if (errorType === LoginOtpVerificationErrorType.OTP_VERIFICATION_FAILED) {
        log.debug('user otp verification failed')

        setCodeResent(false)

        // SET ERROR ON INPUT THAT THE CODE HAS WAS INVALID
        setError('otpCode', {
          message: t(`otp_challenge.expired_code`),
        })

        // EVERY OTHER ERROR SEND TO FAILURE PAGE
      } else onError()
    }
  }

  const { mutate: verifyOtpCode, isLoading } = useLoginOtpVerifification({
    onSuccess,
    onError,
  })

  const onSubmit = handleSubmit(async (fData: FormData): Promise<void> => {
    setCodeResent(false)
    sendEvent('user_signIn_singleFactor_code_submit', {
      eventSource: 'egWealth',
      category: 'signIn',
      subCategory: 'intent',
      verificationType: 'phone',
    })
    await verifyOtpCode({
      otpCode: stripSpaces(fData.otpCode),
    })
  })

  const onResendCode = async (): Promise<void> => {
    setCodeResent(false)
    sendEvent('user_signInReSendVerification_click', {
      eventSource: 'egWealth',
      category: 'signIn',
      subCategory: 'intent',
      verificationType: 'phone',
    })
    await verifyOtpCode({
      resendVerification: true,
    })
  }

  return (
    <div className="egw-container h-full flex flex-col gap-4">
      <h1 className="typo-primary-xl-light text-text-header md:mt-2xl">{t('otp_challenge.title')}</h1>
      <form
        onSubmit={(e) => void onSubmit(e)}
        className="w-full flex-1 flex flex-col justify-start lg:justify-center h-full"
      >
        <div className="flex flex-col text-body text-base gap-4 h-full max-md:justify-between">
          <div className="flex flex-col gap-4">
            <p>{t(`otp_challenge.p1`)}</p>
            <Controller
              name="otpCode"
              control={control}
              rules={{
                required: t('otp_challenge.required'),
                minLength: {
                  // Code is 6 digits + 1 space w/ formatting
                  value: 6,
                  message: t('otp_challenge.length'),
                },
                maxLength: {
                  // Code is 6 digits + 1 space w/ formatting
                  value: 6,
                  message: t('otp_challenge.length'),
                },
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  label={t('otp_challenge.label')}
                  required
                  testId="otpCode_input"
                  error={formErrors.otpCode?.message}
                  maxLength={6}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                />
              )}
            />
            <div className="flex flex-row gap-4">
              <button
                type="button"
                id="resendOtpChallengeCode"
                className="w-fit justify-start text-sky-500 hover:underline typo-primary-base-semibold"
                onClick={() => {
                  void onResendCode()
                }}
              >
                {t(`otp_challenge.resend_code`)}
              </button>
              {codeResent ? (
                <div className="flex items-center">
                  <CheckCircleIcon className="mr-2 h-4 w-4 fill-green-400" />
                  <span className="font-semibold text-sm text-green-600">{t('otp_challenge.code_sent')}</span>
                </div>
              ) : null}
            </div>
          </div>

          <div className="flex grow md:hidden" />
          <Button
            type="submit"
            className="w-full md:w-min mt-4"
            isDisabled={!isValid}
            isLoading={isLoading}
            testId="primaryAction_button"
          >
            {t('otp_challenge.cta')}
          </Button>
        </div>
      </form>
    </div>
  )
}
